import { FormattedMessage, FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { Select, SelectOnChange } from '@liveauctioneers/hammer-ui-core/select';
import { H2 } from '@liveauctioneers/hammer-ui-core/text';
import { Component } from 'react';
import isEqual from 'react-fast-compare';

import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import { WeakRouteLink } from '@/components/RouteLinks/RouteLinks';
import { ReviewComment, ReviewResponse } from '@/types/Review';
import { Seller } from '@/types/Seller';

import { Review } from './Review';

const VIEWS_TO_SHOW = [10, 25, 50, 100];

export type ReviewsProps = {
    dismissReviewsModal: Function;
    filter: string;
    filteredCount: number;
    fullPage: boolean;
    fullPageLink: string;
    helpfulCounts: Record<number, number>;
    onChange: Function;
    page: number;
    pageSize: number;
    reviewComments: ReviewComment[];
    reviews: ReviewResponse[];
    seller: Seller;
    sort: string;
    statuses: Record<number, string[]>;
    submitted: boolean;
    totalReviews: number;
    updateFilter: SelectOnChange;
    updateSort: SelectOnChange;
};

export default class Reviews extends Component<ReviewsProps> {
    shouldComponentUpdate = (nextProps: ReviewsProps) => !isEqual(this.props, nextProps);

    filterOptions = [
        { label: 'All Ratings', value: '' },
        { label: '5 Star Only', value: '5star' },
        { label: '4 Star Only', value: '4star' },
        { label: '3 Star Only', value: '3star' },
        { label: '2 Star Only', value: '2star' },
        { label: '1 Star Only', value: '1star' },
    ];

    sortOptions = [
        { label: 'Most Recent', value: '-created' },
        { label: 'Oldest', value: 'created' },
        { label: 'Highest Rated', value: '-overall' },
        { label: 'Lowest Rated', value: 'overall' },
    ];

    getPageEnds() {
        const { filteredCount, page, pageSize } = this.props;
        let start = (page - 1) * pageSize;
        let end = start + pageSize;

        if (end > filteredCount) {
            end = filteredCount;
        }

        if (start === 0 && end !== 0) {
            start = 1;
        }

        return { end, start };
    }

    closeModal = () => {
        this.props.dismissReviewsModal();
    };

    render() {
        const {
            filter,
            filteredCount,
            fullPage,
            fullPageLink,
            helpfulCounts,
            onChange,
            page,
            pageSize,
            reviewComments,
            reviews,
            seller,
            sort,
            statuses,
            submitted,
            totalReviews,
            updateFilter,
            updateSort,
        } = this.props;
        const commentsByReview = {};
        if (reviewComments) {
            Object.keys(reviewComments).forEach((k) => {
                commentsByReview[reviewComments[k].reviewId]
                    ? commentsByReview[reviewComments[k].reviewId].unshift(reviewComments[k])
                    : (commentsByReview[reviewComments[k].reviewId] = [reviewComments[k]]);
            });
        }
        const reviewDisplays = reviews
            ? reviews.map((houseReview, key) => (
                  <Review
                      comments={commentsByReview[houseReview.reviewId] || []}
                      helpfulCount={helpfulCounts[houseReview.reviewId] || 0}
                      houseReview={houseReview}
                      key={key}
                      seller={seller}
                      statuses={statuses[houseReview.reviewId] || []}
                      submitted={submitted}
                  />
              ))
            : undefined;

        const latestTotalFormatted = (
            <FormattedNumber
                key="latest_total_formatted"
                value={reviews?.length || 0}
            />
        );
        const totalFormatted = (
            <FormattedNumber
                key="total_formatted"
                value={filteredCount || 0}
            />
        );
        const { end, start } = this.getPageEnds();
        const startFormatted = (
            <FormattedNumber
                key="start_count"
                value={start}
            />
        );
        const endFormatted = (
            <FormattedNumber
                key="end_count"
                value={end}
            />
        );

        const title = fullPage ? (
            <FormattedMessage
                id="review.full_page_review_title"
                values={{
                    count: totalFormatted,
                    end: endFormatted,
                    start: startFormatted,
                }}
            />
        ) : reviews?.length === 1 ? (
            <>
                <FormattedMessage id="review.latest_review" />
                <WeakRouteLink
                    className="mb-2xs ml-xs inline-block text-xs tracking-wider underline"
                    onClick={this.closeModal}
                    to={fullPageLink}
                >
                    <FormattedMessage id="review.see_all" />
                </WeakRouteLink>
            </>
        ) : (
            Boolean(reviews?.length) && (
                <>
                    <FormattedMessage
                        id="review.latest_reviews"
                        values={{ count: latestTotalFormatted }}
                    />
                    <WeakRouteLink
                        className="mb-2xs ml-xs inline-block text-xs tracking-wider underline"
                        onClick={this.closeModal}
                        to={fullPageLink}
                    >
                        <FormattedMessage id="review.see_all" />
                    </WeakRouteLink>
                </>
            )
        );

        const fullPageView = (
            <div>
                <div
                    className="mt-xs mb-md flex justify-between border-y border-border-secondary py-lg xsMax:flex-col xsMax:items-center"
                    id="reviews"
                >
                    <H2>{title}</H2>
                </div>
                <Pagination
                    filter={
                        <div className="flex gap-sm xsMax:items-center">
                            <Select
                                className="mb-xs w-full max-w-[260px] xsMax:max-w-full"
                                isInsideLabel
                                labelText={
                                    <>
                                        <FormattedMessage id="filter" />:
                                    </>
                                }
                                name="filter"
                                onChange={(value) => updateFilter(value)}
                                options={this.filterOptions}
                                small
                                value={this.filterOptions.find((op) => op.value === filter)}
                            />
                            <Select
                                className="mb-xs w-full max-w-[260px] xsMax:max-w-full"
                                isInsideLabel
                                labelText={
                                    <>
                                        <FormattedMessage id="sort" />:
                                    </>
                                }
                                name="sort"
                                onChange={(value) => updateSort(value)}
                                options={this.sortOptions}
                                small
                                value={this.sortOptions.find((op) => op.value === sort)}
                            />
                        </div>
                    }
                    onChange={onChange}
                    pageSizeOptions={VIEWS_TO_SHOW}
                    paginationFilter={{
                        page: page,
                        pageSize: pageSize,
                        totalRecords: totalReviews,
                    }}
                >
                    {reviewDisplays}
                </Pagination>
            </div>
        );

        return fullPage ? (
            fullPageView
        ) : (
            <div>
                <H2>{title}</H2>
                {reviewDisplays}
            </div>
        );
    }
}
